import * as THREE from "three";
import { ensureAsset, ensureCoreAssets } from "../core/AssetSource.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FurnitureElement } from "./FurnitureElement.js";
import { AttachmentPoint } from "./AttachmentPoint.js";
import { GhostFurniture } from "./GhostFurniture.js";
import { isModuleToModuleConnection, disposeMesh } from "../utils/geometry.js";
import { DEFAULT_WOOD_TYPE, DEFAULT_FABRIC_CODE, CUSHION_LIKE_COMPONENT_TYPES, AP_MOUNTED_COMPONENT_TYPES, RESEATABLE_COMPONENT_TYPES, normalizeWoodType, aoMapPath } from "../utils/constants.js";
import { SHOW_DEV_TOOLS } from "../config.js";
import { installMethods } from "../utils/installMethods.js";
import { FurnitureCatalog } from "./manager/FurnitureCatalog.js";
import { FurnitureMaterials } from "./manager/FurnitureMaterials.js";
import { LayoutSerializer } from "./manager/LayoutSerializer.js";
import { FurnitureDebugOverlay } from "./manager/FurnitureDebugOverlay.js";
import { SelectionOutline } from "./manager/SelectionOutline.js";

export { findAttachmentPointByOffset } from "./manager/LayoutSerializer.js";

// furniture-hp mirrors furniture 1:1 (scripts/subdivide-models.py), so detail is a directory swap.
// Dev-only: hp meshes are ~15x heavier, so the shipped app always uses the low-poly set.
const STANDARD_MODEL_DIR = "models/furniture";
const HIGH_POLY_MODEL_DIR = "models/furniture-hp";

/** `?highPoly=1` pins the directory for the offline render batches; null = the preference decides. */
function highPolyFromURL(app) {
  if (!SHOW_DEV_TOOLS) return null;
  const flag = app?.urlParams?.get("highPoly");
  if (flag === undefined || flag === null) return null;
  return flag !== "0" && flag !== "false";
}

export class FurnitureManager {
  constructor(app) {
    this.app = app;
    this.scene = app.getScene();
    this.loadingManager = app.getSceneManager().getLoadingManager();
    this.renderer = app.getSceneManager().getRenderer();
    this.furniture = [];
    this.furnitureCollections = {};
    this.productUnavailability = {};
    this.loadedModels = {};
    this._highPolyPinned = highPolyFromURL(app);
    this.modelBaseDir = this._highPolyPinned ? HIGH_POLY_MODEL_DIR : STANDARD_MODEL_DIR;
    this.selectedFurniture = []; // Array for multiple selection support
    // Selected pieces whose components (backrests, cushions, panels) are selected with them.
    this.selectedWithComponents = new Set();
    this.autoCenter = true; // Re-centre on every placement/move; restored from UserPreferences
    this._loadingLayout = false; // True during a batch load: no auto-attach, no auto-centre

    // Unified selection outline
    this.selectionOutline = new SelectionOutline(this.scene);

    // One group for every plus, shown only while a component is dragged (updateAttachmentMarkers).
    this.attachmentMarkers = new THREE.Group();
    this.attachmentMarkers.name = "attachment-markers";
    this.attachmentMarkers.visible = false;
    this.scene.add(this.attachmentMarkers);
    this._attachmentMarkersDirty = false;
    // Bumped on every scene change; a ranged AP's cached free runs are only good for one.
    this.attachmentGeneration = 0;

    // Ghost furniture for placement preview
    this.ghostFurniture = null;

    // GLTF loading for better material preservation
    this.gltfLoader = new GLTFLoader(this.loadingManager);

    // Texture loader for furniture-specific textures (legs, labels)
    this.textureLoader = new THREE.TextureLoader(this.loadingManager);

    // Cache for loaded furniture textures
    this.furnitureTextureCache = {};

    // Current leg texture type (default to "light")
    this.currentLegTextureType = "light";

    // Default wood finish for newly placed wood-surface elements (oiko-wst / oiko-sta).
    // Per-element after placement — only a scene-wide override (?boardColor=) moves it.
    this.currentWoodType = DEFAULT_WOOD_TYPE;

    // Current leg height in cm (default to 8cm)
    this.currentLegHeight = "8";

    // Leg height data from API
    this.legHeightsData = null;

    // Covers API data { options, defaults, categories } keyed by country then urlCode (cart-only).
    this.coversData = null;

    // Selected cover per group, e.g. { module, backrest } (groups via getCoverGroup).
    this.currentCovers = {};

    this._debugCollisionBoxHelpers = [];
  }

  // ===== INITIALIZATION =====
  async init() {
    // Leg/wood/label textures load from synchronous call sites later, so they have to
    // be on disk before any furniture spawns.
    await ensureCoreAssets();

    // Embed mode: skip the editor-only calls, but leg heights are geometry — without them
    // clooods/diagon fall back to 8 and sit 9cm under the floor. Re-seed once the catalog is
    // in, since setDefaultLegHeight() reads furnitureCollections.
    if (this.app?.isEmbedMode) {
      await Promise.all([this.loadFurnitureData(), this.loadLegHeightsData()]);
      this.setDefaultLegHeight();
      return;
    }
    // furniture.json is on the critical path (await); the 3 editor-only backend calls load in
    // the background via productDataReady — consumers null-guard, UIManager re-renders on resolve.
    const furnitureReady = this.loadFurnitureData();
    this.productDataReady = Promise.all([
      this.loadProductUnavailability(),
      this.loadLegHeightsData(),
      this.loadCoversData(),
    ]);
    await furnitureReady;
    // Models are now loaded on-demand when furniture is spawned
  }

  /**
   * Spawn default items for a fresh scene (e.g. zip-preconfigured shelf at top-left)
   */
  async spawnDefaultItems() {
    // Don't spawn if already in the scene (e.g. restored from saved state)
    const alreadyExists = this.furniture.some(f => f.furnitureData?.urlCode === 'zip-preconfigured');
    if (alreadyExists) return;

    let zipData = null;
    const zipCollection = this.furnitureCollections['zip'];
    if (zipCollection && Array.isArray(zipCollection.items)) {
      zipData = zipCollection.items.find(item => item.urlCode === 'zip-preconfigured');
    }
    if (!zipData) return;

    // Respect product unavailability — same check used by the UI to hide items
    const country = this.app.getLocalizationManager?.()?.getCountry?.();
    if (country && !this.isProductAvailable(zipData.id, country)) return;

    const position = new THREE.Vector3(-3.5, 0, -3.5);
    await this.spawnFurniture(zipData, position);
  }

  async loadModel(modelFile) {
    if (!modelFile || modelFile.trim() === "") {
      throw new Error("Empty model file name");
    }

    const fullPath = `${this.modelBaseDir}/${modelFile}`;
    // Stream + cache before GLTFLoader runs. The sibling .bin it requests internally is
    // covered by the URL modifier rather than by this call.
    await ensureAsset(fullPath);

    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        fullPath,
        (gltf) => {
          const object = gltf.scene;
          // Only a stamped bake has a map whose UVs match this geometry (scripts/lib/aoBake.mjs).
          const aoBake = gltf.asset?.extras?.aoBake;
          const aoMap = aoBake ? aoMapPath(fullPath) : null;
          const aoVariantMaps = Object.fromEntries(Object.entries(aoBake?.variants ?? {})
            .map(([variant, pose]) => [variant, aoMapPath(fullPath, pose)]));

          // Scale and position model appropriately
          object.scale.setScalar(1.0); // Normal size

          // Process GLTF materials with proper multi-material support
          object.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;

              // Process materials to set up proper multi-material zones
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material = child.material.map((mat, index) => {
                    const newMat = this.setupGLTFMaterial(mat, child.name, child);
                    return newMat;
                  });
                } else {
                  const newMat = this.setupGLTFMaterial(
                    child.material,
                    child.name,
                    child,
                  );
                  child.material = newMat;
                }
              }

              // Validate and optimize geometry
              if (!child.geometry || !child.geometry.attributes.position) {
                console.warn("Invalid geometry on mesh:", child.name);
                return; // Skip invalid meshes
              }

              // Compute bounding box for culling optimization
              if (!child.geometry.boundingBox) {
                child.geometry.computeBoundingBox();
              }

              // On the geometry so every clone, composite and export copy carries it.
              if (aoMap && child.geometry.attributes.uv1) Object.assign(child.geometry.userData, { aoMap, aoVariantMaps });
            }
          });

          resolve({ mesh: object, animations: gltf.animations || [] });
        },
        (progress) => {
          // Loading progress
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  /**
   * Return the cached model entry for `modelPath`, loading and caching it if needed.
   * Throws on load failure so callers can handle the error with their own message.
   * @returns {{ mesh: THREE.Object3D, animations: THREE.AnimationClip[] }}
   */
  async getOrLoadModel(modelPath) {
    if (!this.loadedModels[modelPath]) {
      this.loadedModels[modelPath] = await this.loadModel(modelPath);
    }
    return this.loadedModels[modelPath];
  }

  /** True while models come from the high-poly set. */
  isHighPoly() {
    return this.modelBaseDir === HIGH_POLY_MODEL_DIR;
  }

  /** False in prod, or when `?highPoly=` pinned the choice for a render batch. */
  canToggleHighPoly() {
    return SHOW_DEV_TOOLS && this._highPolyPinned === null;
  }

  /**
   * Dev-only model-detail switch, ignored when `?highPoly=` pinned it; call reloadModels()
   * afterwards if the scene already has furniture.
   * @param {boolean} on
   * @returns {boolean} whether the directory changed
   */
  setHighPoly(on) {
    if (!SHOW_DEV_TOOLS || this._highPolyPinned !== null) return false;
    const next = on ? HIGH_POLY_MODEL_DIR : STANDARD_MODEL_DIR;
    if (next === this.modelBaseDir) return false;
    this.modelBaseDir = next;
    return true;
  }

  /** Rebuild the scene from the current modelBaseDir; selection is lost, the layout round-trips. */
  async reloadModels() {
    const layout = this.serializeLayout();
    // Clear before disposing the cache: live meshes clone the prototypes and share their geometry.
    this.clearAll();

    for (const entry of Object.values(this.loadedModels)) disposeMesh(entry?.mesh);
    this.loadedModels = {};

    await this.loadLayout(layout);
  }

  async spawnFurniture(furnitureData, position = new THREE.Vector3(0, 0, 0), opts = {}) {
    let modelEntry;
    try {
      modelEntry = await this.getOrLoadModel(furnitureData.model);
    } catch (error) {
      console.error(`Failed to load model ${furnitureData.model}:`, error);
      return null;
    }

    // Exclude the 'id' field from furnitureData to ensure each instance gets a unique UUID
    // (FurnitureElement uses data.id for its instance id). Preserve the catalog item id
    // separately as `catalogId` so save/restore can disambiguate items that share a urlCode
    // (e.g. diagon-bp "Big Pillow" vs diagon-bp-l "Big Pillow L").
    const { id, ...furnitureDataWithoutId } = furnitureData;

    const furnitureElement = new FurnitureElement({
      ...furnitureDataWithoutId,
      catalogId: id,
      mesh: modelEntry.mesh,
      scene: this.scene,
      markerRoot: this.attachmentMarkers,
      position: position,
    });

    this.scene.add(furnitureElement.getMesh());
    this.furniture.push(furnitureElement);
    this.app.getEventManager()?.invalidateFurnitureMeshCache();
    this.invalidateAttachmentMarkers();

    // Apply current leg texture to the new element (cached models keep their original texture)
    const legType = this.isLegTexturesEnabled(furnitureData) ? this.currentLegTextureType : "black";
    furnitureElement.getMesh().traverse((child) => {
      if (child.isMesh && child.material) {
        const materialName = child.material.name ? child.material.name.toLowerCase() : "";
        if (materialName.includes("_wood")) {
          if (furnitureData.collection === 'zip') {
            this.loadShelfWoodTexture(child.material);
          } else if (this.isWoodSurface(furnitureData, child)) {
            const woodType = normalizeWoodType(furnitureElement.woodType || this.currentWoodType);
            furnitureElement.woodType = woodType;
            this.loadWoodTexture(child.material, woodType);
          } else {
            this.loadLegTextures(child.material, legType);
          }
        }
      }
    });

    // Apply default fabric (ET-14) to new furniture if it has fabric zones
    if (this.app.getMaterialManager() && furnitureElement.materialZones && furnitureElement.materialZones.fabric) {
      const defaultFabricId = this.app.getMaterialManager().getFabricIdByCode(DEFAULT_FABRIC_CODE);
      if (defaultFabricId) {
        await this.applyMaterialToFurniture(furnitureElement, defaultFabricId, false);
      }
    }

    // Apply current leg height (no-op on items without legProperties).
    // - Fresh ground placement: `position` is logical (Y=0) → assumeUnlifted: true (add lift).
    // - State restore: `position` is logical (lift subtracted in serializeLayout) → same.
    // - Snap placement: caller passes the ghost's *world* position, which already sits
    //   at the parent module's lifted Y. We must NOT lift again — caller sets `preLifted: true`.
    if (this.currentLegHeight) {
      furnitureElement.applyLegHeight(this.currentLegHeight, {
        animate: false,
        assumeUnlifted: !opts.preLifted,
      });
    }

    // Auto-attach declared child components (e.g. the "R+H" combo spawns an R
    // cushion with an H cushion already seated on it). Skipped during layout
    // restore — saved layouts already contain the child as its own serialized
    // element, and skipped for nested spawns to avoid recursion.
    const autoChildren = furnitureData.properties?.attachChildOnSpawn;
    if (autoChildren?.length && !this._loadingLayout && !opts.skipAutoAttach) {
      await this._attachSpawnChildren(furnitureElement, autoChildren);
    }

    // Trigger debounced save after spawning furniture
    this.debouncedSaveState();

    // New geometry in the scene — refresh the (static) shadow maps / contact blob.
    // Also covers layout load / state restore, where saves are suppressed.
    this.app.getSceneManager()?.requestShadowUpdate();

    return furnitureElement;
  }

  /**
   * Spawn and attach declared child components onto a freshly spawned parent.
   * Each child is seated on the parent's first free attachment point that
   * accepts it. The child becomes a real scene-graph child of the parent, so it
   * follows when the caller subsequently positions/attaches the parent.
   * @param {FurnitureElement} parentElement
   * @param {string[]} childUrlCodes
   */
  async _attachSpawnChildren(parentElement, childUrlCodes) {
    for (const urlCode of childUrlCodes) {
      const childData = this.findCatalogItemByUrlCode(urlCode);
      if (!childData) {
        console.warn(`attachChildOnSpawn: catalog item "${urlCode}" not found`);
        continue;
      }

      const ap = parentElement.attachmentPoints.find(p =>
        !p.isOccupied && AttachmentPoint.admits(p.acceptsComponentTypes, p.acceptsOnly, childData.componentType, urlCode)
      );
      if (!ap) {
        console.warn(`attachChildOnSpawn: no free attachment point for "${urlCode}" on ${parentElement.furnitureData?.urlCode}`);
        continue;
      }

      // Position the child at the AP (world space) before attachTo converts it to
      // local space — mirrors the manual placement flow.
      parentElement.getMesh().updateMatrixWorld(true);
      const connOffset = ap.resolveConnectionOffset(childData);
      const worldPos = ap.getWorldPositionWithOffset(connOffset);
      const orientation = ap.getAttachmentOrientation();

      const child = await this.spawnFurniture(childData, worldPos, {
        preLifted: true,
        skipAutoAttach: true,
      });
      if (!child) continue;

      child.getMesh().quaternion.copy(orientation);
      child.getMesh().position.copy(worldPos);
      child.attachTo(ap);
      child.calculateBoundingBox();

      // Match the parent's current fabric so the combo reads as one unit.
      if (parentElement.fabricId) {
        await this.applyMaterialToFurniture(child, parentElement.fabricId, false);
      }
    }
  }

  /**
   * Build a rest-pose composite mesh for a catalog item that declares
   * `attachChildOnSpawn`, with each child model parented at its attachment-point
   * local offset/rotation. Shared by the ghost preview and catalog thumbnail so
   * both show the same combo the spawn produces.
   *
   * Geometry and materials are shared with the cached source models (Object3D
   * clone() copies neither), so callers must NOT dispose the returned mesh's
   * geometries/materials — doing so would corrupt the model cache.
   * @param {object} furnitureData
   * @returns {Promise<THREE.Object3D|null>} composite mesh, or null when the item
   *   declares no auto-children or the parent model fails to load.
   */
  async buildAutoAttachCompositeMesh(furnitureData) {
    const autoChildren = furnitureData.properties?.attachChildOnSpawn;
    if (!autoChildren?.length) return null;

    let parentEntry;
    try {
      parentEntry = await this.getOrLoadModel(furnitureData.model);
    } catch (error) {
      console.warn(`buildAutoAttachCompositeMesh: parent model ${furnitureData.model} failed`, error);
      return null;
    }
    const root = parentEntry.mesh.clone(true);

    const apDataList = furnitureData.properties?.attachmentPoints ?? [];
    const usedAp = new Set();

    for (const urlCode of autoChildren) {
      const childData = this.findCatalogItemByUrlCode(urlCode);
      if (!childData?.model) continue;

      // Pick the same AP _attachSpawnChildren would: first free one that accepts it.
      let ap = null;
      for (let i = 0; i < apDataList.length; i++) {
        if (usedAp.has(i)) continue;
        const candidate = apDataList[i];
        if (!AttachmentPoint.admits(candidate.accepts, candidate.acceptsOnly, childData.componentType, urlCode)) continue;
        ap = candidate;
        usedAp.add(i);
        break;
      }
      if (!ap) continue;

      let childEntry;
      try {
        childEntry = await this.getOrLoadModel(childData.model);
      } catch (error) {
        continue;
      }
      const childClone = childEntry.mesh.clone(true);

      childClone.rotation.y = AttachmentPoint.restRotationY(ap);

      const pos = new THREE.Vector3(ap.offset.x, ap.offset.y, ap.offset.z);
      const connOffset = ap.connectionOffsetByUrlCode?.[urlCode]
        ?? childData.properties?.connectionOffset;
      if (connOffset) {
        // In the AP's frame, not the rotationY override — as getWorldPositionWithOffset.
        pos.add(new THREE.Vector3(connOffset.x, connOffset.y, connOffset.z)
          .applyAxisAngle(new THREE.Vector3(0, 1, 0), AttachmentPoint.frameRotationY(ap)));
      }
      childClone.position.copy(pos);
      root.add(childClone);
    }

    return root;
  }

  // ===== FURNITURE MANAGEMENT =====
  removeFurniture(furnitureElement) {
    if (!furnitureElement) return;

    // Geometry is leaving the scene — refresh shadows so its shadow clears.
    this.app.getSceneManager()?.requestShadowUpdate();

    // Clear selection if this furniture is selected
    if (this.selectedFurniture === furnitureElement) {
      this.selectedFurniture = null;
    }
    this.selectedWithComponents.delete(furnitureElement);

    // Handle attached children based on connection type
    const attachedChildren = [...furnitureElement.attachedChildren]; // Clone array
    const detachedModuleChildren = [];
    attachedChildren.forEach((child) => {
      if (isModuleToModuleConnection(child, furnitureElement)) {
        // Module-to-module: Just disconnect, don't delete the child module
        child.detachFrom(this.scene);
        detachedModuleChildren.push(child);
      } else {
        // Component: Delete with parent (panels, backrests, cushions)
        this.removeFurniture(child);
      }
    });

    // Detached module children lost their parent — check if they had fragile
    // cushions that depended on the removed element as an adjacent module
    for (const child of detachedModuleChildren) {
      this.removeUnsupportedFragileCushions(child.getAllGroupModules());
    }

    const index = this.furniture.indexOf(furnitureElement);
    if (index !== -1) {
      // Capture parent group before detaching — used for fragile cushion check
      const wasModule = furnitureElement.furnitureData?.componentType === 'module';
      const parentFurniture = furnitureElement.attachedTo?.parentFurniture;

      // A removed cushion may strand a flanked pillow (diagon-mp / diagon-sp) in its group.
      let cushionGroupModules = null;
      if (furnitureElement.furnitureData?.componentType === 'cushion') {
        cushionGroupModules = parentFurniture?.getNearestModule()?.getAllGroupModules() ?? null;
      }

      // If furniture is attached to a parent, detach it first
      if (furnitureElement.attachedTo) {
        furnitureElement.detachFrom(this.scene);
      }

      // Remove from scene (handles both attached and non-attached furniture)
      if (furnitureElement.getMesh().parent) {
        furnitureElement.getMesh().parent.remove(furnitureElement.getMesh());
      }

      // Dispose of all resources
      furnitureElement.dispose();

      // Remove from furniture list
      this.furniture.splice(index, 1);
      this.app.getEventManager()?.invalidateFurnitureMeshCache();
      this.invalidateAttachmentMarkers();

      // After removing a module, check remaining group for cushions that lost
      // their required adjacent module support (fragile cushion removal)
      if (wasModule && parentFurniture) {
        this.removeUnsupportedFragileCushions(parentFurniture.getAllGroupModules());
      }

      // After removing a cushion, a flanked pillow in the same group may have
      // lost a flanking big cushion — re-check and drop it if so.
      if (cushionGroupModules) {
        this.removeUnsupportedFragileCushions(cushionGroupModules);
      }

      // The covers control is driven by what's in scene, so losing the last item that has
      // them has to hide it. Runs once per recursion level, like requestShadowUpdate above.
      this.app.getUIManager()?.updateCoversUI();

      // Trigger debounced save after removing furniture
      this.debouncedSaveState();
    }
  }

  /**
   * Remove cushions whose `requiresAdjacentModule` APs are no longer satisfied.
   * Called after a module is detached or removed from a group — the remaining
   * group members may have cushions that depended on the departed module.
   * @param {Set<FurnitureElement>|FurnitureElement[]} modules - Modules to check
   */
  removeUnsupportedFragileCushions(modules) {
    const toRemove = [];
    for (const module of modules) {
      for (const ap of module.attachmentPoints) {
        if (!ap.isOccupied || !ap.attachedFurniture) continue;

        // Flanked pillows (diagon-mp / diagon-sp): remove when a big cushion is
        // no longer present on both sides of the slot.
        if (ap.requiresFlankingCushions?.length) {
          if (!module.hasFlankingCushions(ap)) toRemove.push(ap.attachedFurniture);
          continue;
        }

        if (!ap.requiresAdjacentModule?.length) continue;

        // Re-check the required adjacent module(s) — single side, corner pair, or any.
        if (!module.hasRequiredAdjacentModule(ap)) {
          toRemove.push(ap.attachedFurniture);
        }
      }
    }
    for (const cushion of toRemove) {
      this.removeFurniture(cushion);
    }
  }

  clearAll() {
    // Remove all furniture (copy array to avoid mutation during iteration)
    const allFurniture = [...this.furniture];
    allFurniture.forEach((furniture) => {
      // Remove from scene
      if (furniture.getMesh().parent) {
        furniture.getMesh().parent.remove(furniture.getMesh());
      }
      // Clear attachedTo before dispose() to prevent detachComponentConnection
      // from re-adding the mesh to the scene (leaves orphaned meshes after clearAll)
      furniture.attachedTo = null;
      // Dispose all resources
      furniture.dispose();
    });

    // Clear the furniture array
    this.furniture = [];
    this.app.getEventManager()?.invalidateFurnitureMeshCache();
    this.invalidateAttachmentMarkers();

    // Scene is now empty — refresh on-demand shadows so old ones don't linger.
    this.app.getSceneManager()?.requestShadowUpdate();

    // Clear selection and outline
    this.selectedFurniture = [];
    this.selectedWithComponents.clear();
    this.updateSelectionOutline();
    this.app.getUIManager()?.updateCoversUI();
  }

  getAllFurniture() {
    return this.furniture;
  }

  // ===== SELECTION MANAGEMENT =====
  /** @param {{withComponents?: boolean}} [options] components share the outline and fabric picks */
  selectFurniture(furniture, multiSelect = false, { withComponents = false } = {}) {
    // Ensure selectedFurniture is always an array (defensive check)
    if (!Array.isArray(this.selectedFurniture)) {
      this.selectedFurniture = [];
    }

    if (!multiSelect) {
      // Deselect ALL furniture (including children) to hide all attachment points
      this.furniture.forEach((f) => {
        if (f.isSelected) {
          f.setSelected(false);
        }
      });
      this.selectedFurniture = [];
      this.selectedWithComponents.clear();
    }

    // Handle selection
    if (furniture === null) {
      // Clear selection
      this.selectedFurniture = [];
      this.selectedWithComponents.clear();
    } else if (multiSelect && this.selectedFurniture.includes(furniture)) {
      // Toggle: deselect if already selected
      furniture.setSelected(false);
      this.selectedFurniture = this.selectedFurniture.filter(f => f !== furniture);
      this.selectedWithComponents.delete(furniture);
    } else {
      // Add to selection
      furniture.setSelected(true);
      if (!this.selectedFurniture.includes(furniture)) {
        this.selectedFurniture.push(furniture);
      }
      if (withComponents) this.selectedWithComponents.add(furniture);
    }

    this.updateSelectionOutline();
  }

  isSelectedWithComponents(furniture) {
    return this.selectedWithComponents.has(furniture);
  }

  /**
   * Rebuild the unified selection outline for all selected furniture.
   * Creates a single outline group in the scene containing BackSide meshes
   * for every selected piece, so they visually merge into one outline.
   */
  updateSelectionOutline() {
    this.selectionOutline?.update(this.getSelectedFurnitureArray(), this.selectedWithComponents);
  }

  getSelectedFurniture() {
    // Return first selected for backward compatibility
    // Handle case where selectedFurniture might be null
    if (!this.selectedFurniture || !Array.isArray(this.selectedFurniture)) {
      return null;
    }
    return this.selectedFurniture.length > 0 ? this.selectedFurniture[0] : null;
  }

  getSelectedFurnitureArray() {
    // Ensure we always return an array
    if (!this.selectedFurniture || !Array.isArray(this.selectedFurniture)) {
      return [];
    }
    return this.selectedFurniture;
  }

  hasMultipleSelected() {
    if (!this.selectedFurniture || !Array.isArray(this.selectedFurniture)) {
      return false;
    }
    return this.selectedFurniture.length > 1;
  }

  // ===== GHOST FURNITURE METHODS =====
  /**
   * Create the snap-preview ghost.
   * @param {object} furnitureData
   * @param {FurnitureElement|null} sourceFurniture - When provided (drag-relocate),
   *   clone from the live mesh so the ghost reflects the piece's current pose
   *   (storage open/closed, variant composite transform). Otherwise clone from
   *   the cached source mesh at GLTF rest pose (catalog spawn).
   */
  async createGhostFurniture(furnitureData, sourceFurniture = null) {
    this.clearGhostFurniture();

    let sourceMesh;
    if (sourceFurniture?.getMesh()) {
      sourceMesh = sourceFurniture.getMesh();
    } else {
      try {
        // Combos that auto-attach a child on spawn (e.g. R+H) preview as the
        // composite so the ghost matches what will be placed.
        if (furnitureData.properties?.attachChildOnSpawn?.length) {
          sourceMesh = await this.buildAutoAttachCompositeMesh(furnitureData);
        }
        if (!sourceMesh) {
          const modelEntry = await this.getOrLoadModel(furnitureData.model);
          sourceMesh = modelEntry.mesh;
        }
      } catch (error) {
        console.error(`Failed to load model for ghost furniture: ${furnitureData.model}`, error);
        return;
      }
    }

    this.ghostFurniture = new GhostFurniture(furnitureData, sourceMesh, {
      sourceIsLive: !!sourceFurniture,
    });
    this.scene.add(this.ghostFurniture.getMesh());
  }

  updateGhostFurniturePosition(x, y, z) {
    if (this.ghostFurniture) {
      this.ghostFurniture.setPosition(x, y, z);
    }
  }

  updateGhostFurnitureRotation(angleY) {
    if (this.ghostFurniture) {
      this.ghostFurniture.setRotation(angleY);
    }
  }

  showGhostFurniture() {
    if (this.ghostFurniture) {
      this.ghostFurniture.show();
    }
  }

  clearGhostFurniture() {
    if (this.ghostFurniture) {
      this.scene.remove(this.ghostFurniture.getMesh());
      this.ghostFurniture.dispose();
      this.ghostFurniture = null;
    }
  }

  getGhostFurniture() {
    return this.ghostFurniture;
  }

  getGhostFurnitureRotation() {
    return this.ghostFurniture ? this.ghostFurniture.getCurrentRotation() : 0;
  }

  // ===== SCENE LAYOUT =====
  /**
   * Drop the whole selection. Centres once after the loop, not inside it: removeFurniture
   * cascades into children, cushions and components.
   * @returns {boolean} whether anything was removed — callers still repaint their own UI.
   */
  deleteSelected() {
    const selected = this.getSelectedFurnitureArray();
    if (selected.length === 0) return false;
    selected.forEach((furniture) => this.removeFurniture(furniture));
    this.selectFurniture(null);
    this.centerIfAuto();
    return true;
  }

  /** Off by default; the toggle lives here so callers don't each test it. */
  centerIfAuto() {
    // A batch load is not a placement — already where the user left it, and N× the work.
    if (this.autoCenter && !this._loadingLayout) this.centerFurnitureInScene();
  }

  setAutoCenter(enabled) {
    this.autoCenter = !!enabled;
  }

  /**
   * Center all furniture at the scene origin (X=0, Z=0), preserving Y.
   * Only world-space meshes (direct scene children = modules) are translated;
   * local-space components follow their parent automatically.
   */
  centerFurnitureInScene() {
    if (this.furniture.length === 0) return;

    const nonZip = this.furniture.filter(f => f.furnitureData?.collection !== 'zip');

    // Compute the combined world-space bounding box, excluding zip shelf
    const box = new THREE.Box3();
    nonZip.forEach(f => box.expandByObject(f.getMesh()));
    if (box.isEmpty()) return;

    const center = box.getCenter(new THREE.Vector3());
    const offset = new THREE.Vector3(-center.x, 0, -center.z);

    // Only translate meshes that are direct scene children (world space)
    this.furniture.forEach(f => {
      if (f.getMesh().parent === this.scene) {
        f.getMesh().position.add(offset);
        f.calculateBoundingBox();
      }
    });

    // Recalculate bounding boxes for local-space pieces that followed their parents
    this.furniture.forEach(f => {
      if (f.getMesh().parent !== this.scene) {
        f.calculateBoundingBox();
      }
    });

    // Rebuild selection outline at the new positions
    this.updateSelectionOutline();

    // Furniture moved — refresh the (no-longer-auto-updating) shadows.
    this.app.getSceneManager()?.requestShadowUpdate();

    this.debouncedSaveState();
  }

  // ===== STATE MANAGEMENT METHODS =====
  /**
   * Trigger a debounced state save via the centralised StateManager.
   * Kept for backward-compatibility with call sites in this file.
   */
  debouncedSaveState() {
    this.app.getStateManager()?.debouncedSave();
  }

  /**
   * Apply a new leg height to every eligible furniture element in the scene.
   * "Eligible" = the item's collection is hasLegs:true AND the item authored
   * a properties.legProperties block. All other items are skipped silently.
   */
  applyLegHeightToAll(legHeight, opts = { animate: true }) {
    this.currentLegHeight = legHeight;
    if (!legHeight) return;
    for (const f of this.furniture) {
      const data = f.furnitureData;
      if (this.furnitureCollections?.[data?.collection]?.hasLegs !== true) continue;
      if (!data?.properties?.legProperties) continue;
      f.applyLegHeight(legHeight, opts);
    }
    // Leg height shifts furniture vertically. Animated changes are tracked frame
    // by frame via isAnimating(), but an instant (animate:false) change moves the
    // geometry in one shot — refresh shadows so they don't go stale.
    this.app.getSceneManager()?.requestShadowUpdate();
  }

  /**
   * Tick all furniture variant tweens. Called from the main render loop.
   * The selection outline mirrors the selected piece's mesh transforms, so
   * rebuild it only while that piece is animating.
   */
  update(deltaTime) {
    let anyAnimating = false;
    for (const f of this.furniture) {
      f.update(deltaTime);
      if (f.isAnimating()) anyAnimating = true;
    }
    this._anyAnimating = anyAnimating;
    // Variant / leg-height tweens move geometry over several frames after the
    // commit — keep shadows tracking the motion until the tween settles.
    if (anyAnimating) this.app.getSceneManager()?.requestShadowUpdate();
    const sel = this.getSelectedFurniture();
    const animating = sel?.isAnimating() ?? false;
    // One extra rebuild after the tween ends, so a fade-in subtree gets a
    // final pass once its _fadeActive flag has been cleared.
    if (animating || this._outlineWasAnimating) {
      this.updateSelectionOutline();
    }
    this._outlineWasAnimating = animating;

    this.updateAttachmentMarkers();
  }

  /** Re-check every plus next frame: placement rules reach across pieces, so any change can move one. */
  invalidateAttachmentMarkers() {
    this._attachmentMarkersDirty = true;
    this.attachmentGeneration++;
  }

  /** Per frame: while a component is dragged (from the bar or re-dragged), draw the pluses it can sit on. */
  updateAttachmentMarkers() {
    if (this.app.isEmbedMode) return;
    // The placement ghost and a re-drag's ghost are both this one; a module's never shows pluses.
    const dragged = this.ghostFurniture?.furnitureData ?? null;
    const dragging = AP_MOUNTED_COMPONENT_TYPES.has(dragged?.componentType);
    this.attachmentMarkers.visible = dragging || this._debugAPsActive === true;
    if (!this.attachmentMarkers.visible) return;

    if (this._attachmentMarkersDirty) {
      this._attachmentMarkersDirty = false;
      for (const f of this.furniture) f.refreshAttachmentPointVisibility();
    }
    // A re-dragged panel only slides along its own AP, so that is the one plus it gets.
    const eventManager = this.app.getEventManager?.();
    const origin = eventManager?.componentDragOriginalAP ?? null;
    const onlyAP = origin && !RESEATABLE_COMPONENT_TYPES.has(dragged.componentType) ? origin : null;
    const runsFor = dragging && eventManager ? (this._runsFor ??= (point, data) => {
      const em = this.app.getEventManager();
      return em.componentFreeRuns(point, data, em.componentDragSubtree);
    }) : null;
    for (const f of this.furniture) f.updateAttachmentPoints(dragging ? dragged : null, onlyAP, runsFor);
  }

  /** Render without the pluses (mid-drag, say): a screenshot is of the design, not the editor. */
  withoutAttachmentMarkers(render) {
    const markers = this.attachmentMarkers;
    const wasVisible = markers.visible;
    markers.visible = false;
    try {
      return render();
    } finally {
      markers.visible = wasVisible;
    }
  }

  // Last frame's tween state, so the render loop can defer a pause without a second sweep.
  hasAnimatingFurniture() {
    return this._anyAnimating === true;
  }

  /**
   * Switch a furniture element to the next variant.
   * Called from UIManager when the user clicks the Transform button.
   * @param {FurnitureElement} furnitureElement
   */
  switchFurnitureVariant(furnitureElement) {
    const variants = furnitureElement.furnitureData?.properties?.variants;
    if (!variants || variants.length < 2) return;
    const collisionSystem = this.app.getCollisionSystem?.() ?? null;
    // Try the next variant in cycle order; if its clearanceBox guard rejects
    // (would intrude on a neighbour), keep advancing so the button always
    // produces a visible state change when at least one variant is reachable.
    // A `skipWhenCushioned` variant (bonbon3-a's Open) is passed over rather than drop a seated pillow.
    const cushioned = furnitureElement.attachedChildren.some(
      (child) => CUSHION_LIKE_COMPONENT_TYPES.has(child.furnitureData?.componentType)
    );
    const startIndex = furnitureElement.variantIndex;
    for (let step = 1; step <= variants.length; step++) {
      const candidate = (startIndex + step) % variants.length;
      if (candidate === startIndex) break;
      if (cushioned && variants[candidate].skipWhenCushioned) continue;
      if (furnitureElement.switchVariant(candidate, collisionSystem)) {
        this.finalizeVariantSwitch(furnitureElement);
        return;
      }
    }
  }

  // Housekeeping after a committed variant switch: drop the components the new variant deletes,
  // then refresh whatever caches their meshes. Saving is a no-op in embed (StateManager guards it).
  /** @param {FurnitureElement} furnitureElement */
  finalizeVariantSwitch(furnitureElement) {
    for (const comp of furnitureElement._pendingDeleteComponents ?? []) {
      this.removeFurniture(comp);
    }
    furnitureElement._pendingDeleteComponents = [];
    this.updateSelectionOutline();
    this.app.getEventManager()?.invalidateFurnitureMeshCache();
    this.app.getStateManager()?.saveSceneState();
  }
}

installMethods(FurnitureManager.prototype, FurnitureCatalog, FurnitureMaterials, LayoutSerializer, FurnitureDebugOverlay);
